import { type DominoEnd, type DominoPlayerDto, type DominoViewDto, isDouble, tileKey } from '@spin/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { http } from '../api/http';
import { Avatar, PlayerName, ScreenHeader, Sheet, Spinner } from '../components/common';
import { DominoTile } from '../components/DominoTile';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { sfx } from '../table/sounds';
import { tg } from '../tg/bridge';

/** The local clock, ticking a few times a second (for the turn timer). */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function Opponent({ p, active, left }: { p: DominoPlayerDto; active: boolean; left: number }) {
  const t = useT();
  return (
    <div
      className={`domino-opponent ${active ? 'active' : ''} ${p.out ? 'out' : ''}`}
      data-testid={`domino-seat-${p.seat}`}
    >
      <span className="domino-avatar">
        <Avatar name={p.name} photoUrl={p.photoUrl} size={44} />
        {active && <i className="domino-timer" style={{ ['--left' as string]: left }} />}
      </span>
      <b className="ellipsis">
        <PlayerName name={p.name} frame={p.frame} vip={p.vip} />
      </b>
      <span className="domino-score">{p.out ? t('domino.out') : t('domino.points', { score: p.score })}</span>
      <span className="domino-backs" aria-label={`${p.tiles}`}>
        {Array.from({ length: Math.min(p.tiles, 7) }, (_, i) => (
          <DominoTile key={i} face="down" size={9} />
        ))}
        {p.tiles > 7 && <small>+{p.tiles - 7}</small>}
      </span>
    </div>
  );
}

/** A domino room: waiting for players, then the game with my hand at the bottom. */
export function DominoGame({ id }: { id: string }) {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const back = useSession((s) => s.back);
  /** The view and the gap between the server's clock and ours when it arrived. */
  const [got, setGot] = useState<{ view: DominoViewDto; offset: number } | null>(null);
  /** A tile that fits both ends, waiting for the player to pick one (for that turn only). */
  const [choice, setChoice] = useState<{ tile: string; turnId: string | null } | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [busy, setBusy] = useState(false);
  const now = useNow();
  const lastPlay = useRef<string | null>(null);
  const view = got?.view ?? null;
  const left = view?.deadline ? Math.max(0, view.deadline - (now + got!.offset)) : 0;
  const choosing = choice && view && choice.turnId === view.turnId ? choice.tile : null;

  const accept = useCallback(
    (v: DominoViewDto) => {
      if (v.id !== id) return;
      setGot({ view: v, offset: v.serverNow - Date.now() });
      const key = v.lastMove ? `${v.round}.${v.chain.length}.${v.lastMove.seat}.${v.lastMove.kind}` : null;
      if (key && key !== lastPlay.current && (v.lastMove?.kind === 'play' || v.lastMove?.kind === 'auto')) {
        sfx.tile((me.soundVolume ?? 70) / 100);
        lastPlay.current = key;
      }
    },
    [id, me.soundVolume],
  );

  useEffect(() => {
    http.get<DominoViewDto>(`/domino/rooms/${id}`).then(accept).catch(reportError);
    const onUpdate = (e: Event) => accept((e as CustomEvent<DominoViewDto>).detail);
    window.addEventListener('spin:domino', onUpdate);
    return () => window.removeEventListener('spin:domino', onUpdate);
  }, [id, accept]);

  const myTurn = Boolean(view && view.turnSeat !== null && view.turnSeat === view.mySeat);
  useEffect(() => {
    if (myTurn) tg.haptic('light');
  }, [myTurn, view?.turnId]);

  const move = async (body: Record<string, unknown>) => {
    if (!view?.turnId || busy) return;
    setBusy(true);
    try {
      accept(await http.post<DominoViewDto>(`/domino/rooms/${id}/move`, { ...body, turnId: view.turnId }));
    } catch (err) {
      reportError(err);
      http
        .get<DominoViewDto>(`/domino/rooms/${id}`)
        .then(accept)
        .catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const tapTile = (key: string) => {
    const option = view?.playable.find((p) => p.tile === key);
    if (!option) return;
    if (option.ends.length === 1) void move({ kind: 'play', tile: key, end: option.ends[0] });
    else setChoice({ tile: key, turnId: view!.turnId });
  };

  const leave = async () => {
    try {
      await http.post(`/domino/rooms/${id}/leave`);
      back();
    } catch (err) {
      reportError(err);
    }
  };

  if (!view) {
    return (
      <div className="screen">
        <ScreenHeader title={t('domino.title')} />
        <Spinner />
      </div>
    );
  }

  const others = view.players.filter((p) => p.seat !== view.mySeat);
  const mine = view.players.find((p) => p.seat === view.mySeat);
  const nameOf = (seat: number) => view.players.find((p) => p.seat === seat)?.name ?? '?';
  const stakeText = view.stake ? t('domino.pot', { pot: view.pot }) : t('domino.free');
  const playable = new Set(view.playable.map((p) => p.tile));
  const roundPause = view.status === 'PLAYING' && view.turnSeat === null && view.lastRound;
  const winner = view.players.find((p) => p.userId === view.winnerUserId);

  let status = '';
  if (view.status === 'PLAYING' && view.turnSeat !== null)
    status = myTurn ? t('domino.your_turn') : t('domino.turn_of', { name: nameOf(view.turnSeat) });
  let last = '';
  if (view.lastMove && view.turnSeat !== null) {
    const name = nameOf(view.lastMove.seat);
    if (view.lastMove.kind === 'draw') last = t('domino.drew', { name, n: view.lastMove.drew ?? 0 });
    else if (view.lastMove.kind === 'pass') last = t('domino.passed', { name });
    else if (view.lastMove.kind === 'auto') last = t('domino.auto', { name });
  }

  return (
    <div className="screen domino-screen" data-testid="domino-game" data-status={view.status}>
      <ScreenHeader title={t('domino.title')} right={<span className="domino-pot">{stakeText}</span>} />

      {view.status === 'WAITING' || view.status === 'CANCELLED' ? (
        <div className="card domino-waiting">
          <p className="subtitle" style={{ margin: 0 }}>
            {view.status === 'CANCELLED'
              ? t('domino.cancelled')
              : t('domino.waiting', { n: view.players.length, seats: view.seats })}
          </p>
          <div className="domino-waiting-seats">
            {Array.from({ length: view.seats }, (_, seat) => {
              const p = view.players.find((x) => x.seat === seat);
              return (
                <span key={seat} className={`domino-waiting-seat ${p ? 'taken' : ''}`}>
                  {p ? (
                    <Avatar name={p.name} photoUrl={p.photoUrl} size={52} />
                  ) : (
                    <i className="domino-seat-empty big" />
                  )}
                  <small className="ellipsis">{p ? p.name : t('domino.empty_seat')}</small>
                </span>
              );
            })}
          </div>
          {view.status === 'WAITING' && (
            <button className="btn btn-block" onClick={() => void leave()} data-testid="domino-leave">
              {t('domino.leave_refund')}
            </button>
          )}
        </div>
      ) : (
        <>
          <div className={`domino-opponents n${others.length}`}>
            {others.map((p) => (
              <Opponent key={p.seat} p={p} active={view.turnSeat === p.seat} left={left / 1000} />
            ))}
          </div>

          <div className="domino-board" data-testid="domino-board">
            <div className="domino-board-head">
              <span>{t('domino.round', { round: view.round, target: view.target })}</span>
              <span className="row" style={{ gap: 5 }}>
                <DominoTile face="down" size={7} />
                {t('domino.boneyard', { n: view.boneyard })}
              </span>
            </div>
            <div className="domino-chain">
              {view.chain.map((tile, i) => (
                <DominoTile
                  key={`${tileKey(tile)}`}
                  tile={tile}
                  lying={!isDouble(tile)}
                  size={20}
                  className={choosing && (i === 0 || i === view.chain.length - 1) ? 'end-glow' : ''}
                />
              ))}
            </div>
          </div>

          <div className="domino-status">
            <b className={myTurn ? 'mine' : ''}>{status}</b>
            {view.turnSeat !== null && (
              <span className="domino-bar">
                <i style={{ width: `${Math.min(100, (left / view.turnMs) * 100)}%` }} />
              </span>
            )}
            {last && <small className="muted">{last}</small>}
            {myTurn && !view.chain.length && view.round === 1 && (
              <small className="muted">{t('domino.first_move')}</small>
            )}
          </div>

          {choosing && (
            <div className="domino-ends">
              <span className="muted">{t('domino.choose_end')}</span>
              {(['L', 'R'] as DominoEnd[]).map((end) => (
                <button
                  key={end}
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => void move({ kind: 'play', tile: choosing, end })}
                >
                  {t(end === 'L' ? 'domino.left' : 'domino.right')}
                </button>
              ))}
            </div>
          )}

          <div className={`domino-hand ${myTurn ? 'my-turn' : ''}`} data-testid="domino-hand">
            {view.hand.map((tile) => {
              const key = tileKey(tile);
              const can = playable.has(key);
              return (
                <button
                  key={key}
                  className={`domino-hand-tile ${can ? 'playable' : ''} ${choosing === key ? 'chosen' : ''}`}
                  disabled={!can || busy}
                  onClick={() => tapTile(key)}
                  data-tile={key}
                >
                  <DominoTile tile={tile} size={30} />
                </button>
              );
            })}
          </div>

          <div className="domino-actions">
            {view.canDraw && (
              <button
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void move({ kind: 'draw' })}
                data-testid="domino-draw"
              >
                {t('domino.draw', { n: view.boneyard })}
              </button>
            )}
            {view.canPass && (
              <button
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void move({ kind: 'pass' })}
                data-testid="domino-pass"
              >
                {t('domino.pass')}
              </button>
            )}
            {view.status === 'PLAYING' && mine && !mine.out && (
              <button className="btn btn-ghost" onClick={() => setConfirmLeave(true)}>
                {t('domino.leave')}
              </button>
            )}
          </div>
        </>
      )}

      {roundPause && view.lastRound && (
        <div className="domino-overlay" data-testid="domino-round">
          <div className="card domino-result">
            <b className="subtitle" style={{ margin: 0 }}>
              {view.lastRound.winnerSeat === null
                ? t('domino.round_tie')
                : t(view.lastRound.blocked ? 'domino.round_blocked' : 'domino.round_won', {
                    name: nameOf(view.lastRound.winnerSeat),
                    points: view.lastRound.points,
                  })}
            </b>
            {view.lastRound.hands.map((h) => (
              <div key={h.seat} className="domino-result-hand">
                <span className="ellipsis">
                  {nameOf(h.seat)} · {h.pips}
                </span>
                <span className="domino-result-tiles">
                  {h.tiles.map((tile) => (
                    <DominoTile key={tileKey(tile)} tile={tile} lying size={12} />
                  ))}
                </span>
              </div>
            ))}
            <small className="muted">{t('domino.next_round', { s: Math.ceil(left / 1000) })}</small>
          </div>
        </div>
      )}

      {view.status === 'FINISHED' && (
        <div className="domino-overlay" data-testid="domino-over">
          <div className="card domino-result">
            <div className="domino-trophy" aria-hidden>
              🏆
            </div>
            <b className="title" style={{ margin: 0 }}>
              {view.winnerUserId === me.id && view.payout
                ? t('domino.you_won', { payout: view.payout })
                : t('domino.game_won', { name: winner?.name ?? '?' })}
            </b>
            <div className="domino-final">
              {[...view.players]
                .sort((a, b) => b.score - a.score)
                .map((p) => (
                  <span key={p.seat} className={p.userId === view.winnerUserId ? 'won' : ''}>
                    {p.name} · {p.out ? t('domino.out') : t('domino.points', { score: p.score })}
                  </span>
                ))}
            </div>
            <button className="btn btn-primary btn-block" onClick={back}>
              {t('domino.to_lobby')}
            </button>
          </div>
        </div>
      )}

      {confirmLeave && (
        <Sheet onClose={() => setConfirmLeave(false)}>
          <p className="subtitle">{t('domino.leave_confirm')}</p>
          <div className="grid-2">
            <button className="btn" onClick={() => setConfirmLeave(false)}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-danger"
              onClick={() => void leave()}
              data-testid="domino-leave-confirm"
            >
              {t('domino.leave')}
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
